import React from "react";
import {
  CardContent,
  Typography,
  Paper,
  useTheme,
  Box,
  Chip,
  Stack,
  alpha,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Edit as EditIcon,
  HistoryEdu as AmendIcon,
  OilBarrel as CargoIcon,
  LocationOn as LocationIcon,
  CalendarMonth as DateIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const TripList = ({ trips, onEditTrip, onAmendTrip, isAmended, readOnly }) => {
  const { t } = useTranslation("currentvoyage");
  const theme = useTheme();

  // Hjälpfunktion för att färglägga operationstyp
  const getOpColor = (type) => {
    if (type === "Loading") return theme.palette.success.main;
    if (type === "Discharging") return theme.palette.info.main;
    return theme.palette.primary.main;
  };

  const getCargoSectionLabel = (trip) => {
    const cargoCount = (trip.cargos || []).filter((cargo) => cargo.cargo).length;
    return cargoCount === 1 ? t("cargo") : t("cargoes");
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Stack spacing={2}>
        {trips.map((trip, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid",
              borderColor: isAmended
                ? alpha(theme.palette.error.main, 0.4)
                : theme.palette.divider,
              bgcolor: theme.palette.background.paper,
            }}
          >
            {/* Header: Rutt och Status */}
            <Box
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: alpha(getOpColor(trip.operationType), 0.08),
                borderBottom: `1px solid ${alpha(
                  getOpColor(trip.operationType),
                  0.1
                )}`,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    bgcolor: getOpColor(trip.operationType),
                    color: "white",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                  }}
                >
                  {trip.operationType}
                </Box>
                <Typography
                  variant="h6"
                  sx={{ fontSize: "1.1rem", fontWeight: 600 }}
                >
                  {trip.to || "Unspecified Destination"}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <DateIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {trip.date}
                </Typography>
              </Stack>
            </Box>

            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={4}>
                {/* Kolumn 1: Terminal & Info */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="bold"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {t("jetty")} / {t("supplier/receiver")}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ mt: 0.5, display: "flex", alignItems: "center" }}
                      >
                        <LocationIcon
                          sx={{ mr: 1, fontSize: 18, color: "primary.main" }}
                        />
                        {trip.jetty || "-"} ({trip.supplierReceiver || "-"})
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="bold"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {t("cpdate")}
                      </Typography>
                      <Typography variant="body2">
                        {trip.cpDated || "-"}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>

                {/* Kolumn 2: Cargo (Tanker focus) */}
                <Grid size={{ xs: 12, md: 5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ textTransform: "uppercase", mb: 1, display: "block" }}
                  >
                    {getCargoSectionLabel(trip)}
                  </Typography>
                  <Stack spacing={1}>
                    {trip.cargos
                      .filter((c) => c.cargo)
                      .map((cargo, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.action.hover, 0.1),
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            border: "1px solid",
                            borderColor: theme.palette.divider,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <CargoIcon
                              sx={{ color: "text.secondary", fontSize: 20 }}
                            />
                            <Typography variant="body2" fontWeight="500">
                              {cargo.cargo}
                            </Typography>
                          </Stack>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: "bold",
                              marginLeft: 1,
                            }}
                          >
                            {cargo.quantity}{" "}
                            <span style={{ opacity: 0.9 }}>{cargo.unit}</span>
                          </Typography>
                        </Box>
                      ))}
                  </Stack>
                </Grid>

                {/* Kolumn 3: Crew & Actions */}
                <Grid sx={{ borderLeft: { md: `1px solid ${theme.palette.divider}`, }, pl: { md: 3, xs: 0, }, pt: { xs: 2, md: 0, }, mt: { xs: 2, md: 0, }, borderTop: { xs: `1px solid ${theme.palette.divider}`, md: "none", }, }} size={{ xs: 12, md: 3 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ textTransform: "uppercase", mb: 1, display: "block" }}
                  >
                    Responsibility
                  </Typography>
                  <Typography variant="body2">
                    <strong>M:</strong> {trip.crew.masterName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>CO:</strong> {trip.crew.chiefOfficer}
                  </Typography>

                  {!isAmended && (
                    <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                      <Chip
                        icon={<EditIcon style={{ fontSize: 16 }} />}
                        label="Edit"
                        onClick={readOnly ? undefined : () => onEditTrip(trip)}
                        clickable={!readOnly}
                        disabled={readOnly}
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        icon={<AmendIcon style={{ fontSize: 16 }} />}
                        label="Amend"
                        onClick={
                          readOnly ? undefined : () => onAmendTrip(trip.tripId)
                        }
                        clickable={!readOnly}
                        disabled={readOnly}
                        color="error"
                        variant="outlined"
                        size="small"
                      />
                    </Stack>
                  )}
                </Grid>
              </Grid>

              {isAmended && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.error.main, 0.05),
                    border: `1px dashed ${theme.palette.error.main}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="error.main"
                  >
                    REASON FOR AMENDMENT
                  </Typography>
                  <Typography variant="body2">{trip.amendReason}</Typography>
                </Box>
              )}
            </CardContent>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default TripList;
